/* eslint-disable no-console -- debug logging */
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAppSelector } from '@/hooks';

import { selectChat } from '../store/chat';

const MESSAGE_DRAFT_STORAGE_KEY = 'deepsearch_input_draft';
const LEGACY_SEARCH_TEXT_KEY = 'searchText';

export interface MessageDraft {
  searchText: string;
  location: string;
  education: string;
  profession: string;
  selectedUseCaseId?: string;
}

type DetailedSearchData = Pick<
  MessageDraft,
  'location' | 'education' | 'profession'
>;

interface MessageDraftContextType {
  searchText: string | null;
  setSearchText: (text: string | null) => void;
  detailedSearchData: DetailedSearchData;
  setDetailedSearchData: (data: DetailedSearchData) => void;
  updateDetailedSearchData: (updates: Partial<DetailedSearchData>) => void;
  selectedUseCaseId: string | undefined;
  setSelectedUseCaseId: (id: string | undefined) => void;
  saveMessageDraft: (draft: MessageDraft) => void;
  clearDraft: () => void;
}

const MessageDraftContext = createContext<MessageDraftContextType | null>(null);

interface MessageDraftProviderProps {
  children: ReactNode;
}

function loadDraftFromStorage(): MessageDraft | null {
  try {
    const stored = localStorage.getItem(MESSAGE_DRAFT_STORAGE_KEY);
    if (stored !== null) {
      return JSON.parse(stored) as MessageDraft;
    }
    const legacyText = localStorage.getItem(LEGACY_SEARCH_TEXT_KEY);
    if (legacyText !== null && legacyText !== '') {
      localStorage.removeItem(LEGACY_SEARCH_TEXT_KEY);
      return {
        searchText: legacyText,
        location: '',
        education: '',
        profession: '',
      };
    }
    return null;
  } catch {
    return null;
  }
}

function saveDraftToStorage(draft: MessageDraft): void {
  try {
    if (
      draft.searchText === '' &&
      draft.location === '' &&
      draft.education === '' &&
      draft.profession === '' &&
      draft.selectedUseCaseId === undefined
    ) {
      localStorage.removeItem(MESSAGE_DRAFT_STORAGE_KEY);
    } else {
      localStorage.setItem(MESSAGE_DRAFT_STORAGE_KEY, JSON.stringify(draft));
    }
  } catch {}
}

export function clearStoredMessageDraft(): void {
  localStorage.removeItem(MESSAGE_DRAFT_STORAGE_KEY);
  localStorage.removeItem(LEGACY_SEARCH_TEXT_KEY);
}

export function MessageDraftProvider({
  children,
}: MessageDraftProviderProps): ReactNode {
  const chat = useAppSelector(selectChat);
  const previousConversationIdRef = useRef<string | undefined>(undefined);

  const [searchText, setSearchTextState] = useState<string | null>(() => {
    const draft = loadDraftFromStorage();
    return draft?.searchText ?? null;
  });
  const [detailedSearchData, setDetailedSearchData] =
    useState<DetailedSearchData>({
      location: '',
      education: '',
      profession: '',
    });
  const [selectedUseCaseId, setSelectedUseCaseIdState] = useState<
    string | undefined
  >(undefined);

  const setSelectedUseCaseId = useCallback((id: string | undefined): void => {
    setSelectedUseCaseIdState(id);
    const draft = loadDraftFromStorage() ?? {
      searchText: '',
      location: '',
      education: '',
      profession: '',
    };
    saveDraftToStorage({ ...draft, selectedUseCaseId: id });
  }, []);

  const setSearchText = useCallback((text: string | null): void => {
    setSearchTextState(text);
    const draft = loadDraftFromStorage();
    const baseDraft: MessageDraft = draft ?? {
      searchText: '',
      location: '',
      education: '',
      profession: '',
    };
    saveDraftToStorage({
      ...baseDraft,
      searchText: text ?? '',
    });
  }, []);

  const saveMessageDraft = useCallback(
    (draft: MessageDraft): void => {
      saveDraftToStorage(draft);
      setSearchTextState(draft.searchText ?? null);
      setDetailedSearchData({
        location: draft.location ?? '',
        education: draft.education ?? '',
        profession: draft.profession ?? '',
      });
      setSelectedUseCaseId(draft.selectedUseCaseId);
    },
    [setSelectedUseCaseId],
  );

  const clearDraft = useCallback((): void => {
    clearStoredMessageDraft();
    setSearchTextState(null);
    setDetailedSearchData({
      location: '',
      education: '',
      profession: '',
    });
    setSelectedUseCaseIdState(undefined);
  }, []);

  useEffect(() => {
    const conversationId = chat.activeConversation?.id;
    const hasActiveConversation =
      conversationId != null && conversationId.length > 0;
    const hadConversation =
      previousConversationIdRef.current != null &&
      previousConversationIdRef.current.length > 0;
    const isNewChat = hadConversation && !hasActiveConversation;

    previousConversationIdRef.current = conversationId;

    if (isNewChat) {
      clearStoredMessageDraft();
      setSearchTextState(null);
      setDetailedSearchData({
        location: '',
        education: '',
        profession: '',
      });
      setSelectedUseCaseIdState(undefined);
      return;
    }

    if (hasActiveConversation) {
      setSearchTextState(null);
      setDetailedSearchData({
        location: '',
        education: '',
        profession: '',
      });
      setSelectedUseCaseIdState(undefined);
      return;
    }

    const draft = loadDraftFromStorage();
    if (draft === null) return;

    setSearchTextState(draft.searchText ?? null);
    setDetailedSearchData({
      location: draft.location ?? '',
      education: draft.education ?? '',
      profession: draft.profession ?? '',
    });
    setSelectedUseCaseIdState(draft.selectedUseCaseId);
  }, [chat.activeConversation?.id]);

  const updateDetailedSearchData = useCallback(
    (updates: Partial<DetailedSearchData>): void => {
      setDetailedSearchData((prev) => {
        const next = { ...prev, ...updates };
        const draft = loadDraftFromStorage() ?? {
          searchText: '',
          location: '',
          education: '',
          profession: '',
        };
        saveDraftToStorage({ ...draft, ...next });
        return next;
      });
    },
    [],
  );

  const value: MessageDraftContextType = {
    searchText,
    setSearchText,
    detailedSearchData,
    setDetailedSearchData,
    updateDetailedSearchData,
    selectedUseCaseId,
    setSelectedUseCaseId,
    saveMessageDraft,
    clearDraft,
  };

  return (
    <MessageDraftContext.Provider value={value}>
      {children}
    </MessageDraftContext.Provider>
  );
}

export function useMessageDraft(): MessageDraftContextType {
  const context = useContext(MessageDraftContext);
  if (context === null) {
    throw new Error(
      'useMessageDraft must be used within a MessageDraftProvider',
    );
  }
  return context;
}
